"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";

import { Controller } from "react-hook-form";
import Checkbox from "@/components/atoms/FormComponents/Checkbox";
import Input from "@/components/atoms/FormComponents/Input/Input";
import Select from "@/components/atoms/FormComponents/Select";
import SubmitButton from "@/components/atoms/FormComponents/SubmitButton";
import cn from "clsx";
import styles from "./FaqForm.module.scss";
import ApplicationFormRedirectLoader from "../../components/molecules/ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import Markdown from "@/components/atoms/Markdown";
import { ALL_COUNTRIES } from "@/context/countries";
import { disabledButtonPaths } from "../../components/molecules/Forms/LandingPageConnectModalForm/utils";
import ConsentFormPara from "@/components/common/utils/consentFormPara";
import ThankyouForInterestSection from "../../components/molecules/Forms/LandingPageConnectModalForm/Thankyou";
import CountryDropdownGlobal from "@/components/atoms/FormComponents/CustomDropdownGlobal/CustomDropdownGlobal";
import { usePathname } from "next/navigation";
import { useFaqLandingFormController } from "./controller/useFaqLandingFormController";
import type { LandingConnectModalFormProps } from "./model/faqLandingForm.types";

const getCssConfig = () => ({
  formWrapper: "h-[100%]",
  firstName: cn(styles.inputWrapper, "flex gap-4"),
  phoneNumber: cn(styles.commonMargins),
  email: cn(styles.commonMargins, styles.emailContainer, "flex gap-4"),
  termsConditions: cn(styles.commonMargins, "flex items-start gap-[17px]"),
});

export default function FaqLandingForm(props: LandingConnectModalFormProps) {
  const path = usePathname();
  const {
    control,
    errors,
    isSubmitting,
    isOTPLoading,
    isFormValid,
    disabledButton,
    isCampusImmersionPage,
    isHeaderHidden,
    isApplicationRedirectLoaderDisabled,
    isThankYouBannerHidden,
    loadingCount,
    ctaText,
    experienceList,
    message,
    isWebinar,
    isModal,
    setRefs,
    handleFieldChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
  } = useFaqLandingFormController(props);

  const cssConfig = getCssConfig();

  return (
    <div
      id="#leadform"
      className={cn(
        styles.root,
        "h-full flex flex-col justify-between",
        { [styles.greyBackground]: isWebinar },
        { [styles.whiteBackground]: isModal },
      )}
    >
      {!isApplicationRedirectLoaderDisabled ? (
        <div className={styles.applicationLoader}>
          <ApplicationFormRedirectLoader
            redirectTime={loadingCount}
            text=""
            isDarkForm={true}
          />
        </div>
      ) : !isThankYouBannerHidden ? (
        <ThankyouForInterestSection />
      ) : (
        <InpSafeForm formRef={setRefs} onSubmit={handleFormSubmit}>
          {!isHeaderHidden && (
            <Markdown className={styles.formHeading}>
              {isWebinar ? ctaText : "Enquire Now"}
            </Markdown>
          )}
          <div className={cssConfig.formWrapper}>
            <div className={cssConfig.firstName}>
              <div
                className={
                  isCampusImmersionPage
                    ? styles.widthInputboxFirstName
                    : "w-[100%]"
                }
              >
                <label htmlFor="full_name">
                  full name<span className="text-xl">*</span>
                </label>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      isDark={true}
                      placeholder="Enter your full name"
                      value={field.value ?? ""}
                      onChangeFunc={(value: string) =>
                        handleFieldChange("fullName", value)
                      }
                      onBlur={() => {
                        field.onBlur();
                        handleFieldBlur(
                          "fullName",
                          "",
                          fieldState.error?.message || errors.fullName,
                        );
                      }}
                      id="full_name"
                    />
                  )}
                />
                <p className={cn(styles.error)}>{errors.fullName}</p>
              </div>
              <div
                className={
                  isCampusImmersionPage ? styles.widthInputbox : "hidden"
                }
              >
                <label htmlFor="work_exp">
                  Work Experience<span className="text-xl">*</span>
                </label>
                <div>
                  <Controller
                    name="workExperience"
                    control={control}
                    render={({ field }) => (
                      <Select
                        isFullWidth={true}
                        value={field.value ?? ""}
                        onChangeFunc={(value: string) =>
                          handleFieldChange("workExperience", value)
                        }
                        options={experienceList}
                        disabled={disabledButton}
                        id="work_exp"
                      />
                    )}
                  />
                </div>
                <p className={cn(styles.error)}>{errors.workExperience}</p>
              </div>
            </div>
            <div className={cssConfig.phoneNumber}>
              <div className="w-[100%]">
                <label htmlFor="phone_num">
                  phone number <span className="text-xl">*</span>
                </label>
                <div className="flex gap-[6px]">
                  <div>
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <CountryDropdownGlobal
                          onChangeFunc={(value: string) =>
                            handleFieldChange("countryCode", value)
                          }
                          countries={ALL_COUNTRIES}
                          countryCode={field.value}
                          updateCountryCode={updateCountryCode}
                          additionalStyle={
                            "h-[36px] xl:mt-[2px] !rounded-[10px] !text-[.9rem]"
                          }
                          isDark={true}
                          disabled={
                            disabledButtonPaths.includes(path) ||
                            isSubmitting ||
                            isOTPLoading
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-full">
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Input
                          isDark={true}
                          placeholder="Enter your no."
                          value={field.value ?? ""}
                          onChangeFunc={handlePhoneNumberChange}
                          onBlur={() => {
                            field.onBlur();
                            handleFieldBlur(
                              "phoneNumber",
                              "",
                              fieldState.error?.message || errors.phoneNumber,
                            );
                          }}
                          id="phone_num"
                          isTel
                        />
                      )}
                    />
                  </div>
                </div>
                <p className={cn(styles.error)}>
                  {errors.countryCode || errors.phoneNumber}
                </p>
              </div>
            </div>

            <div className={cssConfig.email}>
              <div className="w-[100%]">
                <label htmlFor="email_id">
                  email id <span className="text-xl">*</span>
                </label>
                <Controller
                  name="emailId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      isDark={true}
                      placeholder="abc@xyz.com"
                      value={field.value ?? ""}
                      onChangeFunc={(value: string) =>
                        handleFieldChange("emailId", value)
                      }
                      onBlur={() => {
                        field.onBlur();
                        handleFieldBlur(
                          "emailId",
                          "",
                          fieldState.error?.message || errors.emailId,
                        );
                      }}
                      id="email_id"
                    />
                  )}
                />
                <p className={cn(styles.error)}>{errors.emailId}</p>
              </div>
            </div>
          </div>

          <div className={cssConfig.termsConditions}>
            <div className="shrink-0">
              <Controller
                name="isTermsAccepted"
                control={control}
                render={({ field, fieldState }) => (
                  <Checkbox
                    name="consent"
                    isDark={true}
                    value={field.value}
                    onChangeFunc={(value: boolean) =>
                      handleFieldChange("isTermsAccepted", value)
                    }
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur(
                        "isTermsAccepted",
                        "",
                        fieldState.error?.message ||
                          errors?.isTermsAccepted ||
                          "NA",
                      );
                    }}
                    id="agree_checkbox"
                  />
                )}
              />
            </div>
            <p className={cn(styles.authorization)}>
              <ConsentFormPara />
            </p>
          </div>

          <SubmitButton
            text={isSubmitting || isOTPLoading ? "Submitting" : "Submit"}
            isDisabled={isSubmitting || isOTPLoading}
            bgColor="#ffc907"
            textColor="#1a325d"
            id={"submit_button"}
          />
          {message && message}
        </InpSafeForm>
      )}
    </div>
  );
}
